import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  isPushSupported,
  getVapidPublicKey,
  currentSubscription,
  enablePush,
  disablePush,
} from '../lib/push/pushClient';

type State = 'checking' | 'unavailable' | 'off' | 'on' | 'busy';

/**
 * "Notify me on this device" — turns on browser push (an additive nudge to open
 * the app; the message itself never travels in a notification). Hidden entirely
 * when push isn't provisioned (no VAPID key yet) or the browser can't do push,
 * so it never dangles a control that would fail.
 */
export function PushToggle() {
  const { api } = useAuth();
  const [state, setState] = useState<State>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) {
        if (!cancelled) setState('unavailable');
        return;
      }
      const vapid = await getVapidPublicKey(api);
      if (cancelled) return;
      if (vapid === null) {
        setState('unavailable'); // deploy-gated: no VAPID key provisioned
        return;
      }
      const sub = await currentSubscription();
      if (!cancelled) setState(sub !== null ? 'on' : 'off');
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const toggle = useCallback(
    async (wasOn: boolean) => {
      setError(null);
      setState('busy');
      try {
        if (wasOn) {
          await disablePush(api);
          setState('off');
        } else {
          const ok = await enablePush(api);
          setState(ok ? 'on' : 'off');
          if (!ok) setError('Turn on notifications for this site to enable them.');
        }
      } catch {
        setError('We could not change your notification setting.');
        setState('off');
      }
    },
    [api],
  );

  if (state === 'checking' || state === 'unavailable') return null;

  return (
    <div className="you__block">
      <p className="vault__section-label">Notifications</p>
      <p className="vault__sub" style={{ marginTop: 8 }}>
        {state === 'on'
          ? 'This device will nudge you when there’s something to see. Your messages stay sealed — a notification only opens the app.'
          : 'Get a gentle nudge on this device when there’s something to see. No message content is ever sent in a notification.'}
      </p>
      {error !== null && (
        <p role="alert" className="guard__alert" style={{ marginTop: 8 }}>
          {error}
        </p>
      )}
      <div className="la-cta-wrap" style={{ marginTop: 16 }}>
        <button className="la-btn-secondary" disabled={state === 'busy'} onClick={() => void toggle(state === 'on')}>
          {state === 'busy' ? 'Working…' : state === 'on' ? 'Turn off notifications' : 'Turn on notifications'}
        </button>
      </div>
    </div>
  );
}
